import React from 'react';
import { Quote } from 'lucide-react';

/**
 * CustomLandingSection — renders one of the admin-defined dynamic sections
 * (headline_banner | image_row | testimonials | video_embed) with
 * per-section layout tuning read from platform_settings.landing_section_layout.
 *
 * Falls back gracefully when required props are missing so a partially-configured
 * section never crashes the home page.
 */
const CustomLandingSection = ({ section, layout }) => {
  if (!section || !section.kind) return null;
  const style = {
    maxWidth: layout?.max_width_px ? `${layout.max_width_px}px` : undefined,
    marginLeft: layout?.max_width_px ? 'auto' : undefined,
    marginRight: layout?.max_width_px ? 'auto' : undefined,
    paddingTop: layout?.padding_y != null ? `${layout.padding_y}px` : undefined,
    paddingBottom: layout?.padding_y != null ? `${layout.padding_y}px` : undefined,
    paddingLeft: layout?.padding_x != null ? `${layout.padding_x}px` : undefined,
    paddingRight: layout?.padding_x != null ? `${layout.padding_x}px` : undefined,
  };

  if (section.kind === 'headline_banner') {
    const bg = section.background_color || '#0f172a';
    const fg = section.text_color || '#ffffff';
    return (
      <div
        className="mt-4 rounded-xl text-center"
        style={{ ...style, backgroundColor: bg, color: fg }}
        data-testid={`custom-section-headline-${section.id}`}
      >
        <div className="px-4 py-6">
          <div className="text-xl sm:text-2xl font-extrabold tracking-tight">{section.title || 'Headline'}</div>
          {section.subtitle && <div className="mt-1 text-sm opacity-90">{section.subtitle}</div>}
          {section.cta_label && section.cta_link && (
            <a
              href={section.cta_link}
              className="inline-block mt-3 px-4 py-2 rounded-full font-semibold text-sm"
              style={{ backgroundColor: fg, color: bg }}
            >
              {section.cta_label}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (section.kind === 'image_row') {
    const cols = Math.max(1, Math.min(6, section.columns || 3));
    const gridClass = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3',
                        4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6' }[cols];
    const images = Array.isArray(section.images) ? section.images.filter((i) => i.url) : [];
    if (images.length === 0) return null;
    return (
      <div className="mt-4" style={style} data-testid={`custom-section-image-${section.id}`}>
        {section.title && <div className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-2">{section.title}</div>}
        <div className={`grid ${gridClass} gap-2`}>
          {images.map((img, i) => {
            const inner = (
              <img
                src={img.url}
                alt={img.alt || ''}
                className="w-full h-32 object-cover rounded-lg"
                loading="lazy"
              />
            );
            return img.link ? (
              <a key={i} href={img.link} className="block hover:opacity-90 transition">{inner}</a>
            ) : (
              <div key={i}>{inner}</div>
            );
          })}
        </div>
      </div>
    );
  }

  if (section.kind === 'testimonials') {
    const items = Array.isArray(section.items) ? section.items.filter((t) => t.quote) : [];
    if (items.length === 0) return null;
    return (
      <div className="mt-4" style={style} data-testid={`custom-section-testimonials-${section.id}`}>
        {section.title && <div className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-2">{section.title}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {items.slice(0, 6).map((t, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <Quote className="h-4 w-4 text-slate-400 mb-2" />
              <div className="text-sm text-slate-700 leading-snug">&ldquo;{t.quote}&rdquo;</div>
              <div className="mt-2 flex items-center gap-2">
                {t.avatar && <img src={t.avatar} alt="" className="h-6 w-6 rounded-full" />}
                <div className="text-xs font-semibold text-slate-800">{t.author || 'Anonymous'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.kind === 'video_embed') {
    const url = section.video_url;
    if (!url) return null;
    // Normalise YouTube / Vimeo to embed URLs so admins can paste watch links.
    let embed = url;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
    if (ytMatch) embed = `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vmMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vmMatch) embed = `https://player.vimeo.com/video/${vmMatch[1]}`;
    return (
      <div className="mt-4" style={style} data-testid={`custom-section-video-${section.id}`}>
        {section.title && <div className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-2">{section.title}</div>}
        <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden bg-slate-900">
          <iframe
            src={embed}
            title={section.title || 'Video'}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  return null;
};

export default CustomLandingSection;
